/**
 * ContractorAddressesViewElement — blok „Adresy kontrahenta" jako przenośny klocek
 * kreatora layoutu (konfigurowalne zakładki, kartoteka Kontrahenci).
 *
 * W builderze renderuje się jako PEŁNOWYMIAROWA formatka listy przez wspólny
 * `FormatkaReorderList`: stały zestaw kolumn CORE adresu, każda z uchwytem przeciągania
 * (zmiana kolejności → `settings.columnOrder`) i ikonką oka (ukryj/pokaż →
 * `settings.hiddenColumnIds`). Read-side (`AddressesDynamicTab`) czyta OBA ustawienia.
 * `fullWidth: true`.
 */

import { MapPin } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';
import { applyOrder, parseSlugList, reorderSlugs } from '@/lib/kartoteki/formatka-order';

import { FormatkaReorderList } from './FormatkaReorderList';

/** Stałe kolumny CORE listy adresów (slug = pole helper-encji `addresses`). */
const ADDRESS_COLUMNS: ReadonlyArray<{ slug: string; labelKey: string; defaultLabel: string }> = [
  {
    slug: 'kind',
    labelKey: 'tenant.contractors.addressesBlock.columns.kind',
    defaultLabel: 'Typ adresu',
  },
  {
    slug: 'street',
    labelKey: 'tenant.contractors.addressesBlock.columns.street',
    defaultLabel: 'Ulica',
  },
  {
    slug: 'building_no',
    labelKey: 'tenant.contractors.addressesBlock.columns.buildingNo',
    defaultLabel: 'Nr budynku',
  },
  {
    slug: 'apartment_no',
    labelKey: 'tenant.contractors.addressesBlock.columns.apartmentNo',
    defaultLabel: 'Nr lokalu',
  },
  {
    slug: 'postal_code',
    labelKey: 'tenant.contractors.addressesBlock.columns.postalCode',
    defaultLabel: 'Kod pocztowy',
  },
  {
    slug: 'city',
    labelKey: 'tenant.contractors.addressesBlock.columns.city',
    defaultLabel: 'Miejscowość',
  },
  {
    slug: 'country_code',
    labelKey: 'tenant.contractors.addressesBlock.columns.country',
    defaultLabel: 'Kraj',
  },
  {
    slug: 'region',
    labelKey: 'tenant.contractors.addressesBlock.columns.region',
    defaultLabel: 'Województwo',
  },
];

const COLUMN_BY_SLUG = new Map(ADDRESS_COLUMNS.map((c) => [c.slug, c]));

/** Bezpieczny odczyt `settings.hiddenColumnIds` → lista slugów ukrytych kolumn. */
export function parseHiddenAddressColumns(settings: Record<string, unknown> | undefined): string[] {
  const raw = settings?.hiddenColumnIds;
  if (!Array.isArray(raw)) return [];
  return raw.filter((s): s is string => typeof s === 'string');
}

export const ContractorAddressesViewElement: LayoutElementDefinition = {
  type: 'contractor_addresses_view',
  category: 'tresc',
  isContainer: false,
  fullWidth: true,
  label: 'Adresy kontrahenta',
  icon: 'map-pin',
  allowedModules: ['contractors'],
  defaultSettings: () => ({}),
  render: ({ element, mode, onSettingsChange, systemHideOnly }) => {
    const hidden = new Set(parseHiddenAddressColumns(element.settings));
    const orderedSlugs = applyOrder(
      ADDRESS_COLUMNS.map((c) => c.slug),
      parseSlugList(element.settings, 'columnOrder'),
    );
    const canHide = mode === 'builder' && !!onSettingsChange;
    // Systemowy layout: tylko ukrywanie (bez reorderu), oko przebija inertny canvas.
    const canReorder = canHide && !systemHideOnly;

    const items = orderedSlugs.map((slug) => {
      const col = COLUMN_BY_SLUG.get(slug);
      return {
        slug,
        label: col ? i18n.t(col.labelKey, { defaultValue: col.defaultLabel }) : slug,
        hint: i18n.t('tenant.contractors.addressesBlock.columnKind', { defaultValue: 'kolumna' }),
      };
    });

    return (
      <FormatkaReorderList
        elementId={element.id}
        items={items}
        hiddenSlugs={hidden}
        canHide={canHide}
        canReorder={canReorder}
        pierceLock={!!systemHideOnly}
        onReorder={(active, over) =>
          onSettingsChange?.({ columnOrder: reorderSlugs(orderedSlugs, active, over) })
        }
        onToggleHidden={(slug) => {
          const next = new Set(hidden);
          if (next.has(slug)) next.delete(slug);
          else next.add(slug);
          onSettingsChange?.({ hiddenColumnIds: Array.from(next) });
        }}
        header={
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <MapPin className="h-4 w-4 text-text-muted" aria-hidden={true} />
            {i18n.t('tenant.contractors.addressesBlock.paletteLabel', {
              defaultValue: 'Adresy kontrahenta',
            })}
            <span className="text-text-muted">
              {' · '}
              {i18n.t('tenant.contractors.addressesBlock.columnsHint', {
                defaultValue: 'kolumny listy',
              })}
            </span>
          </p>
        }
        hiddenLabel={i18n.t('tenant.contractors.addressesBlock.fieldHidden', {
          defaultValue: 'ukryte',
        })}
        labels={{
          hide: i18n.t('tenant.contractors.addressesBlock.hideField', {
            defaultValue: 'Ukryj kolumnę',
          }),
          show: i18n.t('tenant.contractors.addressesBlock.showField', {
            defaultValue: 'Pokaż kolumnę',
          }),
          drag: i18n.t('tenant.formatkaReorder.dragHint', {
            defaultValue: 'Przeciągnij, aby zmienić kolejność',
          }),
        }}
        testId={`contractor-addresses-view-element-${element.id}`}
        rowTestIdPrefix="contractor-addresses-column"
        elementType="contractor_addresses_view"
      />
    );
  },
};
